import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  InputAdornment,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarIcon from "@mui/icons-material/CalendarMonthOutlined";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import CustomTypography from "../../globalStyles/fonts";
import { isMobile } from "../../globalStyles/mobile";
import { historicalInventoryService } from "../../utils/services/historicalInventoryService";
import { productService } from "../../utils/services/productService";
import { wmsService } from "../../utils/services/wmsProvider";
import ClientAutocomplete from "../clientAutocomplete";
import AutocompleteWithPagination from "../autocomplete-with-pagination";
import { fieldSx } from "../../pages/manage-catalog/constants";

const init_data = {
  client: null,
  product: null,
  warehouse: null,
  sku: "",
  date: null,
  quantity: "",
  in_hand: "",
  in_transit: "",
};

const HistoricalInventoryModal = ({
  open,
  handleClose,
  onAddData,
  selectedInventory,
}) => {
  const [formData, setFormData] = useState(init_data);
  const [formErrors, setFormErrors] = useState({});
  const client_id = localStorage.getItem("client_id");
  const [showCalendarFor, setShowCalendarFor] = useState(null);
  const calendarRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        event.target.getAttribute("name") !== "date-field"
      ) {
        setShowCalendarFor(null);
      }
    }

    if (showCalendarFor) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCalendarFor]);

  useEffect(() => {
    if (selectedInventory) {
      setFormData({
        client: selectedInventory.client || null,
        product: selectedInventory.product || null,
        warehouse: selectedInventory.warehouse || null,
        sku: selectedInventory.sku || "",
        date: selectedInventory.date ? new Date(selectedInventory.date) : null,
        quantity: String(selectedInventory.quantity ?? ""),
        in_hand: String(selectedInventory.in_hand ?? ""),
        in_transit: String(selectedInventory.in_transit ?? ""),
      });
    } else {
      resetForm();
    }
  }, [selectedInventory]);

  const resetForm = () => {
    setFormData(init_data);
    setFormErrors({});
    setShowCalendarFor(null);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.client && !client_id) errors.client = "Client is required";
    if (!formData.product) errors.product = "Product is required";
    if (!formData.sku) errors.sku = "SKU is required";
    if (!formData.warehouse) errors.warehouse = "Warehouse is required";
    if (!formData.date) errors.date = "Date is required";
    if (formData.quantity === "" || isNaN(formData.quantity))
      errors.quantity = "Quantity is required";
    if (formData.in_hand === "" || isNaN(formData.in_hand))
      errors.in_hand = "On Hand is required";
    if (formData.in_transit === "" || isNaN(formData.in_transit))
      errors.in_transit = "In Transit is required";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildPayload = () => ({
    client: client_id || formData.client?._id,
    product: formData.product?._id,
    warehouse: formData.warehouse?._id,
    sku: formData.sku,
    date: formData.date,
    quantity: Number(formData.quantity),
    in_hand: Number(formData.in_hand),
    in_transit: Number(formData.in_transit),
  });

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      await historicalInventoryService.create(buildPayload());
      toast.success("Historical inventory record created successfully!");
      onAddData && onAddData();
      handleClose();
      resetForm();
    } catch (error) {
      toast.error("Failed to create historical inventory record.");
      console.error(error);
    }
  };

  const handleEditData = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      await historicalInventoryService.edit(
        selectedInventory._id,
        buildPayload(),
      );
      toast.success("Historical inventory record updated successfully!");
      onAddData && onAddData();
      handleClose();
      resetForm();
    } catch (error) {
      toast.error("Failed to update historical inventory record.");
      console.error(error);
    }
  };

  const onChangeDate = (date) => {
    if (!showCalendarFor) return;
    handleInputChange(showCalendarFor, date);
    setShowCalendarFor(null);
  };

  const renderDateField = (field, label, required) => (
    <Grid item xs={12} sm={6}>
      <CustomTypography fontSize="14px" fontWeight={400} fontFamily={"poppins"}>
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </CustomTypography>
      <TextField
        name="date-field"
        margin="dense"
        size="small"
        placeholder={`Select ${label}`}
        value={formData[field] ? dayjs(formData[field]).format("YYYY-MM-DD") : ""}
        onFocus={() => setShowCalendarFor(field)}
        inputProps={{ readOnly: true }}
        fullWidth
        variant="outlined"
        sx={fieldSx}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconButton onClick={() => setShowCalendarFor(field)} size="small">
                <CalendarIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        error={!!formErrors[field]}
        helperText={formErrors[field]}
      />
    </Grid>
  );

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleClose();
        resetForm();
      }}
      maxWidth="md"
      fullWidth
      aria-labelledby="form-dialog-title"
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <DialogTitle
          id="form-dialog-title"
          style={{
            fontFamily: "Poppins",
            fontSize: isMobile() ? 16 : 24,
            fontWeight: 500,
            color: "var(--text-primary)",
          }}
        >
          {selectedInventory ? "Edit" : "Create"} Historical Inventory
        </DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Divider style={{ marginBottom: 10 }} />
      <DialogContent>
        <Grid container spacing={2}>
          {!client_id && (
            <Grid item xs={12} sm={6}>
              <CustomTypography
                fontSize="14px"
                fontWeight={400}
                fontFamily={"poppins"}
              >
                Client <span style={{ color: "red" }}>*</span>
              </CustomTypography>
              <ClientAutocomplete
                width={"100%"}
                hideLabel
                selectedClient={formData.client}
                onClientChange={(client) =>
                  handleInputChange("client", client)
                }
                errorMessage={formErrors.client}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize="14px" fontWeight={400} fontFamily={"poppins"}>
              Product <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <AutocompleteWithPagination
              hideLabel
              width={"100%"}
              selectedClient={formData.client || { _id: client_id }}
              selectedItem={formData.product}
              onDataChange={(product) => handleInputChange("product", product)}
              optionKey={"name"}
              apiEndPoint={(limit, skip, search, client) =>
                productService.fetchNames(search, client, limit, skip)
              }
              errorMessage={formErrors.product}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize="14px" fontWeight={400} fontFamily={"poppins"}>
              SKU <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <TextField
              name="sku"
              size="small"
              placeholder="Enter SKU"
              value={formData.sku}
              onChange={(e) => handleInputChange("sku", e.target.value)}
              fullWidth
              margin="dense"
              error={!!formErrors.sku}
              helperText={formErrors.sku}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize="14px" fontWeight={400} fontFamily={"poppins"}>
              Warehouse Code <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <AutocompleteWithPagination
              hideLabel
              width={"100%"}
              selectedClient={formData.client || { _id: client_id }}
              selectedItem={formData.warehouse}
              onDataChange={(warehouse) =>
                handleInputChange("warehouse", warehouse)
              }
              optionKey={"warehouse_code"}
              apiEndPoint={(limit, skip, search, client) =>
                wmsService.fetch(client, limit, skip)
              }
              errorMessage={formErrors.warehouse}
            />
          </Grid>
          {renderDateField("date", "Date", true)}
          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize="14px" fontWeight={400} fontFamily={"poppins"}>
              Quantity <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <TextField
              name="quantity"
              type="number"
              size="small"
              placeholder="Enter Quantity"
              value={formData.quantity}
              onChange={(e) => handleInputChange("quantity", e.target.value)}
              fullWidth
              margin="dense"
              error={!!formErrors.quantity}
              helperText={formErrors.quantity}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize="14px" fontWeight={400} fontFamily={"poppins"}>
              On Hand <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <TextField
              name="in_hand"
              type="number"
              size="small"
              placeholder="Enter On Hand"
              value={formData.in_hand}
              onChange={(e) => handleInputChange("in_hand", e.target.value)}
              fullWidth
              margin="dense"
              error={!!formErrors.in_hand}
              helperText={formErrors.in_hand}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize="14px" fontWeight={400} fontFamily={"poppins"}>
              In Transit <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <TextField
              name="in_transit"
              type="number"
              size="small"
              placeholder="Enter In Transit"
              value={formData.in_transit}
              onChange={(e) => handleInputChange("in_transit", e.target.value)}
              fullWidth
              margin="dense"
              error={!!formErrors.in_transit}
              helperText={formErrors.in_transit}
              sx={fieldSx}
            />
          </Grid>
        </Grid>

        {showCalendarFor && (
          <Box
            ref={calendarRef}
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 3000,
              bgcolor: "var(--bg-paper)",
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
              borderRadius: 1,
              p: 0.5,
            }}
          >
            <Calendar
              value={formData[showCalendarFor]}
              onChange={(date) => onChangeDate(date)}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ paddingBottom: 15, paddingRight: 15 }}>
        <Button
          style={{
            fontFamily: "Poppins",
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
          }}
          variant="outlined"
          onClick={() => {
            handleClose();
            resetForm();
          }}
          color="primary"
        >
          Cancel
        </Button>
        <Button
          onClick={(e) =>
            selectedInventory ? handleEditData(e) : handleSave(e)
          }
          color="primary"
          variant="contained"
          style={{
            color: "#FFFFFF",
            fontFamily: "Poppins",
            fontSize: 14,
            fontWeight: 600,
            width: 100,
            textTransform: "none",
          }}
        >
          {selectedInventory ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HistoricalInventoryModal;
